import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { Loader, Center } from '@/components/ui';
import { CALLCENTRE_PAGE_ACCESS as access } from '@/hooks/callcentre/useCallCentrePermissions';
import { CallCentreGate } from './components/CallCentreGate';

// Eager load the dashboard for instant access
import CallCentreDashboard from './dashboard';

// Lazy load every other page
const CampaignsPage = lazy(() => import('./campaigns'));
const CampaignWizardPage = lazy(() => import('./campaigns/wizard'));
const CampaignDetailPage = lazy(() => import('./campaigns/detail'));
const ImportReviewPage = lazy(() => import('./imports'));
const ContactsPage = lazy(() => import('./contacts'));
const DoNotCallPage = lazy(() => import('./do-not-call'));
const DetailChangesPage = lazy(() => import('./detail-changes'));
const JourneyPage = lazy(() => import('./journey'));
const HandoffsPage = lazy(() => import('./handoffs'));
const CallersPage = lazy(() => import('./callers'));
const ReportsPage = lazy(() => import('./reports'));
const SettingsPage = lazy(() => import('./settings'));
const TelephonyPage = lazy(() => import('./telephony'));
const GuideBuilderPage = lazy(() => import('./guide-builder'));

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

const isId = (v: string | undefined): v is string => !!v && /^\d+$/.test(v);

/** /campaigns/:id only for numeric ids; anything else goes back to the list. */
function CampaignDetailRoute() {
  const { id } = useParams();
  if (!isId(id)) return <Navigate to="/app/callcentre/campaigns" replace />;
  return (
    <CallCentreGate allow={access.campaignDetail}>
      <CampaignDetailPage key={id} campaignId={Number(id)} />
    </CallCentreGate>
  );
}

/** /campaigns/:id/setup: resume the wizard for a campaign. */
function CampaignSetupRoute() {
  const { id } = useParams();
  if (!isId(id)) return <Navigate to="/app/callcentre/campaigns" replace />;
  return (
    <CallCentreGate allow={access.campaignSetup}>
      <CampaignWizardPage key={id} campaignId={Number(id)} />
    </CallCentreGate>
  );
}

/** /campaigns/:id/guide: the full-screen conversation guide builder for a campaign. */
function CampaignGuideRoute() {
  const { id } = useParams();
  if (!isId(id)) return <Navigate to="/app/callcentre/campaigns" replace />;
  return (
    <CallCentreGate allow={access.campaignDetail}>
      <GuideBuilderPage key={id} campaignId={Number(id)} />
    </CallCentreGate>
  );
}

/** /settings/campaign-types/:typeId/guide: the builder for a campaign type's starter guide. */
function StarterGuideRoute() {
  const { typeId } = useParams();
  if (!isId(typeId)) return <Navigate to="/app/callcentre/settings?tab=types" replace />;
  return (
    <CallCentreGate allow={access.settings}>
      <GuideBuilderPage key={typeId} campaignTypeId={Number(typeId)} />
    </CallCentreGate>
  );
}

function ImportReviewRoute() {
  const { batchId } = useParams();
  if (!isId(batchId)) return <Navigate to="/app/callcentre/campaigns" replace />;
  return (
    <CallCentreGate allow={access.importReview}>
      <ImportReviewPage key={batchId} batchId={Number(batchId)} />
    </CallCentreGate>
  );
}

/** /contacts/:id opens the Contacts page with that contact's drawer (reminder links). */
function ContactRoute() {
  const { id } = useParams();
  if (!isId(id)) return <Navigate to="/app/callcentre/contacts" replace />;
  return (
    <CallCentreGate allow={access.contacts}>
      <ContactsPage key={id} initialContactId={Number(id)} />
    </CallCentreGate>
  );
}

/**
 * Call Centre ("Call campaigns") Routes
 *
 * Module access (Admin / CallCentreAdmin / CallCentreSupervisor) is the domain gate; inside, pages gate on
 * capabilities (role OR CallCentre.* right) via CallCentreGate. The per-page predicates live in
 * CALLCENTRE_PAGE_ACCESS, which the sidebar menu (callCentreMenu.tsx) reads too.
 *
 * Reminder links from the backend land on /campaigns, /campaigns/:id, /journey, /handoffs and /detail-changes
 * (plus /contacts/:id and /imports/:batchId). Stage 3: /telephony (the phone system; /stations opens its Desk
 * stations tab). The conversation guide builder is full screen: /campaigns/:id/guide and
 * /settings/campaign-types/:typeId/guide (a campaign type's starter).
 */
export default function CallCentreManagementRoutes() {
  return (
    <MainLayout domainKey="callcentre">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<CallCentreGate allow={access.dashboard}><CallCentreDashboard /></CallCentreGate>} />

          {/* Calling */}
          <Route path="/campaigns" element={<CallCentreGate allow={access.campaigns}><CampaignsPage /></CallCentreGate>} />
          <Route path="/campaigns/new" element={<CallCentreGate allow={access.campaignNew}><CampaignWizardPage /></CallCentreGate>} />
          <Route path="/campaigns/:id/setup" element={<CampaignSetupRoute />} />
          <Route path="/campaigns/:id" element={<CampaignDetailRoute />} />
          <Route path="/campaigns/:id/guide" element={<CampaignGuideRoute />} />
          <Route path="/imports/:batchId" element={<ImportReviewRoute />} />
          <Route path="/contacts" element={<CallCentreGate allow={access.contacts}><ContactsPage /></CallCentreGate>} />
          <Route path="/contacts/:id" element={<ContactRoute />} />
          <Route path="/do-not-call" element={<CallCentreGate allow={access.doNotCall}><DoNotCallPage /></CallCentreGate>} />
          <Route path="/detail-changes" element={<CallCentreGate allow={access.detailChanges}><DetailChangesPage /></CallCentreGate>} />

          {/* Follow-up */}
          <Route path="/journey" element={<CallCentreGate allow={access.journey}><JourneyPage /></CallCentreGate>} />
          <Route path="/handoffs" element={<CallCentreGate allow={access.handoffs}><HandoffsPage /></CallCentreGate>} />

          {/* People and results */}
          <Route path="/callers" element={<CallCentreGate allow={access.callers}><CallersPage /></CallCentreGate>} />
          <Route path="/reports" element={<CallCentreGate allow={access.reports}><ReportsPage /></CallCentreGate>} />

          {/* Administration */}
          <Route path="/settings" element={<CallCentreGate allow={access.settings}><SettingsPage /></CallCentreGate>} />
          <Route path="/settings/campaign-types/:typeId/guide" element={<StarterGuideRoute />} />
          <Route path="/telephony" element={<CallCentreGate allow={access.telephony}><TelephonyPage /></CallCentreGate>} />
          <Route path="/stations" element={<Navigate to="/app/callcentre/telephony?tab=stations" replace />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/app/callcentre" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
